import { useState } from "react";
import { Loader2, MessageSquare, Phone, FileText, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useNegacaoLead, useVoltarATrabalhar, type ProximoPasso } from "@/hooks/useLeadNegado";

const PASSOS: { valor: ProximoPasso; titulo: string; texto: string; botao: string; Icon: typeof Phone }[] = [
  { valor: "ligar", titulo: "Ligar agora", texto: "Abre o softphone. O lead vai para Tentando contato.", botao: "Reabrir e ligar", Icon: Phone },
  { valor: "whatsapp", titulo: "Chamar no WhatsApp", texto: "Abre a conversa. Ao mandar a mensagem, vai para Tentando contato.", botao: "Reabrir e chamar no WhatsApp", Icon: MessageSquare },
  { valor: "proposta", titulo: "Montar proposta", texto: "Vai para a aba Proposta. Ao enviar, vira Proposta.", botao: "Reabrir e montar proposta", Icon: FileText },
];

const BTN = "h-[29px] rounded-full border border-border bg-card px-3 text-[12px] font-semibold text-foreground hover:border-primary/40 hover:text-primary";

function dataHora(iso: string | null) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

interface Props {
  leadId: string;
  motivoNegacao?: string | null;
  confirmando: boolean;
  onConfirmandoChange: (v: boolean) => void;
  temTelefone: boolean;
  onLigar: () => void;
  onWhatsapp: (janela: Window | null) => void;
  onProposta: () => void;
}

export function LeadNegadoVoltarTrabalhar({ leadId, motivoNegacao, confirmando, onConfirmandoChange, temTelefone, onLigar, onWhatsapp, onProposta }: Props) {
  const { data: negacao } = useNegacaoLead(leadId, true);
  const voltar = useVoltarATrabalhar(leadId);
  const [proximo, setProximo] = useState<ProximoPasso>(temTelefone ? "ligar" : "proposta");
  const [porque, setPorque] = useState("");
  const passo = PASSOS.find((p) => p.valor === proximo)!;

  const confirmar = async () => {
    if (porque.trim().length < 3) {
      toast.error("Escreva o que mudou para voltar a trabalhar o lead.");
      return;
    }
    const janela = proximo === "whatsapp" ? window.open("", "_blank") : null;
    try {
      await voltar.mutateAsync({ motivo: porque.trim(), proximo });
      onConfirmandoChange(false);
      setPorque("");
      if (proximo === "ligar") onLigar();
      else if (proximo === "whatsapp") onWhatsapp(janela);
      else onProposta();
      toast.success("Lead reaberto. O motivo antigo e o novo ficaram na Timeline.");
    } catch (e) {
      janela?.close();
      toast.error((e as Error)?.message || "Não foi possível reabrir o lead.");
    }
  };

  const quem = negacao?.quem;
  const quando = dataHora(negacao?.quando ?? null);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-border border-l-[3px] border-l-primary bg-primary/[0.06] py-3 pl-4 pr-3">
        <div className="min-w-0">
          <div className="text-[11px] font-extrabold tracking-[0.06em] text-primary">LEAD NEGADO</div>
          <p className="mt-1 text-[13px] leading-snug text-foreground">
            <b>Motivo:</b> {motivoNegacao?.trim() ? `"${motivoNegacao.trim()}"` : "sem motivo registrado"}
          </p>
          {(quem || quando) && (
            <div className="mt-1 text-[11.5px] text-muted-foreground">
              Negado{quem ? ` por ${quem}` : ""}{quando ? ` em ${quando}` : ""}
            </div>
          )}
        </div>
        {!confirmando && (
          <Button variant="outline" className={BTN} onClick={() => onConfirmandoChange(true)}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Voltar a trabalhar
          </Button>
        )}
      </div>

      {confirmando && (
        <div className="space-y-3 rounded-xl border border-border bg-card p-4">
          <div className="text-[14px] font-bold text-foreground">Voltar a trabalhar este lead</div>
          <div className="grid gap-2 sm:grid-cols-3" role="group" aria-label="Próximo passo">
            {PASSOS.map((p) => {
              const desligado = p.valor !== "proposta" && !temTelefone;
              const ativo = proximo === p.valor;
              return (
                <button
                  key={p.valor}
                  type="button"
                  disabled={desligado}
                  aria-pressed={ativo}
                  onClick={() => setProximo(p.valor)}
                  className={`rounded-[10px] border bg-card px-3 py-2.5 text-left text-[12.5px] leading-snug transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${ativo ? "border-primary shadow-[inset_0_0_0_1px_hsl(var(--primary))]" : "border-border hover:border-primary/40"}`}
                >
                  <span className="mb-0.5 flex items-center gap-1.5 text-[13px] font-bold text-foreground">
                    <p.Icon className="h-3.5 w-3.5" />
                    {p.titulo}
                  </span>
                  <span className="text-muted-foreground">{desligado ? "Lead sem telefone." : p.texto}</span>
                </button>
              );
            })}
          </div>
          <div className="space-y-1">
            <label htmlFor="voltar-trabalhar-porque" className="text-[10.5px] font-extrabold tracking-[0.06em] text-muted-foreground">
              O QUE MUDOU (OBRIGATÓRIO)
            </label>
            <Textarea
              id="voltar-trabalhar-porque"
              value={porque}
              onChange={(e) => setPorque(e.target.value)}
              placeholder="Ex.: chegou março, o cliente pediu retorno."
              className="min-h-[60px] text-[13px]"
            />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" className={BTN} onClick={() => onConfirmandoChange(false)} disabled={voltar.isPending}>
              Cancelar
            </Button>
            <Button
              className="h-9 rounded-full border-0 bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-4 text-[12.5px] font-bold text-white hover:brightness-110 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
              onClick={confirmar}
              disabled={voltar.isPending}
            >
              {voltar.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <passo.Icon className="mr-2 h-4 w-4" />}
              {passo.botao}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
